import { useStripe } from "@stripe/react-stripe-js";
import { useTranslation } from "react-i18next";
import { useStripeSubscription } from "~/api/billing/queries";
import { ISubscriptionStripe } from "~/api/billing/types";
import handleErrors from "~/helpers/handleErrors";
import { IPaymentValues } from "~/types/billing";
import { useShopifyPayment } from "./useShopifyPayment";
import { toast } from "~/components/toast";
import { useUTM } from "~/hooks/useUTM";

type Props = {
  planId: string;
  isUserShopify: boolean;
  upgradeWithStore: boolean;
};

export const usePayment = ({ planId, isUserShopify, upgradeWithStore = false  }: Props) => {
  const { mutateAsync: stripeSubscription } = useStripeSubscription();
  const { t } = useTranslation();
  const stripe = useStripe();
  const payShopify = useShopifyPayment(planId);
  const utms = useUTM()

  const payStripe = async (values?: IPaymentValues) => {
    const payload: ISubscriptionStripe = { plan_id: planId };
    if (values?.name) payload.name = values?.name;
    if (values?.payment_method_id)
      payload.payment_method_id = values?.payment_method_id;
    if (values?.setup_intent) payload.setup_intent = values?.setup_intent;
    if (values?.promo_code_id) payload.promo_code_id = values?.promo_code_id;
    if (upgradeWithStore) payload.upgrade_with_store = true;
    try {
      let utmParams: any = utms;

      if (!utms?.campaign && !utms?.source && !utms?.medium && !utms?.term && !utms?.content) {
        utmParams = null;
      }

      const response = await stripeSubscription({
        ...payload,
        utms: utmParams,
      });

 
      const searchParams = new URLSearchParams(window.location.search);
      searchParams.set("plan_id", planId);
      let returnURL = `${window.location.origin}${window.location.pathname}?${searchParams.toString()}`;

      if (response?.client_secret && stripe) {
        const confirmPaymentResponse = await stripe.confirmPayment({
          clientSecret: response.client_secret,
          confirmParams: {
            return_url: returnURL
          }
        })

        if (confirmPaymentResponse.error) {
          toast.warning(
            confirmPaymentResponse.error.message ?? t("checkout.went_wrong")
          );
          return confirmPaymentResponse?.error;
        }

        return null;
      }
    } catch {
      toast.warning(t("checkout.went_wrong"));
      return "error";
    }

    return null;
  };

  const pay = async (values?: IPaymentValues) => {
    if (isUserShopify) {
      return payShopify();
    }

    const toastMessages = {
      loading: t("checkout.loading"),
      success: t("checkout.success"),
      error: t("checkout.error"),
    };
    const { response } = await handleErrors(
      () => payStripe(values),
      toastMessages
    );
    return response;
  };

  return { pay, payStripe, payShopify };
};
